<template>
    <div>
        <div v-if="locked" class="session-shield" role="dialog" aria-modal="true" aria-label="Подтверждение входа" @keydown.tab="trapFocus">
            <div class="session-shield__card argus-glass-dialog">
                <p class="alert alert-warning" role="alert">{{ shared.message }} Несохранённая форма остаётся в этой вкладке.</p>
                <PlatformLogin v-if="scope === 'platform'" embedded @authenticated="check"/>
                <Login v-else embedded @authenticated="check"/>
            </div>
        </div>
        <template v-else-if="needsTelegram">
            <button class="telegram-reminder btn btn-warning btn-sm" @click="showReminder = true">{{ status.telegram.state === 'blocked' ? 'Разблокируйте бота Telegram' : 'Подключите Telegram' }}</button>
            <div v-if="showReminder" class="telegram-reminder-dialog" role="dialog" aria-modal="true" aria-label="Подключение Telegram">
                <div class="session-shield__card argus-glass-dialog">
                    <TelegramChallenge v-if="enrollment" :initial="enrollment" :scope="scope" @connected="connected" @cancel="enrollment = null"/>
                    <template v-else>
                        <h3>{{ status.telegram.state === 'blocked' ? 'Бот заблокирован' : 'Подключите Telegram' }}</h3>
                        <p v-if="status.telegram.state === 'blocked'">Бот не может присылать вам уведомления Аргуса. Разблокируйте его в Telegram — мы покажем, как это сделать.</p>
                        <p v-else>Подключите Telegram, чтобы получать уведомления Аргуса. Дальше будет ссылка на бота и QR-код для телефона.</p>
                        <button class="btn btn-primary" :disabled="busy" @click="enroll">{{ busy ? 'Открываем…' : status.telegram.state === 'blocked' ? 'Как разблокировать бота' : 'Подключить Telegram' }}</button>
                    </template>
                    <p v-if="error" role="alert" class="alert alert-danger mt-2">{{ error }}</p>
                    <button class="btn btn-outline-secondary mt-3" @click="dismiss">Напомнить через 30 минут работы</button>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import Login from '@/views/Login.vue';
import PlatformLogin from '@/views/Platform/PlatformLogin.vue';
import TelegramChallenge from './TelegramChallenge.vue';
import {securityState, securityRequest, refreshSession, claims, identityOf, lockSession, tokenKey} from '@/utils/sessionSecurity';
import decode from 'jwt-decode';
export default {
    components: {Login, PlatformLogin, TelegramChallenge},
    data() { return {shared: securityState, status: null, enrollment: null, showReminder: false, error: '', busy: false,
        timer: null, lastInteraction: Date.now(), lastActivitySent: 0, lastCheck: 0, checking: false}; },
    computed: {
        scope() { return this.$route.path.startsWith('/platform') ? 'platform' : 'staff'; },
        onLogin() { return ['/login', '/platform/login', '/telegram-datepicker'].includes(this.$route.path); },
        locked() { return !this.onLogin && this.shared.locked && this.shared.scope === this.scope; },
        needsTelegram() { return !this.onLogin && this.status && this.status.telegram.state !== 'connected'; },
        reminderKey() { return 'telegram-reminder:' + identityOf(claims(this.scope)); }
    },
    watch: {
        locked(value) { document.body.classList.toggle('argus-security-locked', value); this.$emit('locked', value); },
        '$route.path'() { this.status = null; this.enrollment = null; this.check(); }
    },
    mounted() {
        ['pointerdown', 'keydown', 'input', 'touchstart', 'wheel'].forEach(type => window.addEventListener(type, this.activity, {passive: true}));
        window.addEventListener('focus', this.check); document.addEventListener('visibilitychange', this.visible);
        window.addEventListener('storage', this.storageChanged); window.addEventListener('argus:session', this.sessionChanged);
        this.timer = setInterval(this.tick, 1000); this.check();
    },
    beforeDestroy() {
        clearInterval(this.timer);
        ['pointerdown', 'keydown', 'input', 'touchstart', 'wheel'].forEach(type => window.removeEventListener(type, this.activity));
        window.removeEventListener('focus', this.check); document.removeEventListener('visibilitychange', this.visible);
        window.removeEventListener('storage', this.storageChanged); window.removeEventListener('argus:session', this.sessionChanged);
        document.body.classList.remove('argus-security-locked');
    },
    methods: {
        activity(event) { if (event.isTrusted) this.lastInteraction = Date.now(); },
        visible() { if (!document.hidden) this.check(); },
        sessionChanged(event) {
            const {scope, session} = event.detail;
            if (scope === 'staff') {
                this.$store.commit('setToken', session.accessToken); this.$store.commit('setRefreshToken', 'cookie');
                this.$store.dispatch('setAuthorizationHeader', session.accessToken); this.$store.dispatch('updateAbility', this);
            }
            if (scope === this.scope) this.status = session.security ? Object.assign({}, session.security, {session_expires_at: session.session_expires_at}) : this.status;
        },
        storageChanged(event) {
            if (event.key !== tokenKey(this.scope)) return;
            if (!event.newValue) {
                try { this.shared.identity = identityOf(decode(event.oldValue)); } catch (_) { /* Unknown former session. */ }
                lockSession(this.scope, null); return;
            }
            // Another tab may refresh this session; never replace a form with another user's identity.
            if (this.scope === 'staff') {
                const oldIdentity = identityOf(this.$store.getters.currentUser && {userId: this.$store.getters.currentUser.userId, company: this.$store.getters.currentUser.company});
                if (oldIdentity && oldIdentity !== identityOf(claims('staff'))) { location.reload(); return; }
                this.$store.commit('setToken', event.newValue); this.$store.dispatch('setAuthorizationHeader', event.newValue);
            }
            this.check();
        },
        async check() {
            if (this.checking || this.onLogin || this.locked || !claims(this.scope) || document.hidden) return;
            this.checking = true; this.lastCheck = Date.now();
            try {
                this.status = await securityRequest(this.scope, 'status');
                if (this.status.telegram.state === 'connected') { this.showReminder = false; this.enrollment = null; }
                else {
                    const saved = this.reminder();
                    if (!saved || saved.remaining <= 0) this.showReminder = true;
                }
            } catch (_) { /* The request helper locks on an authentication failure, never on a transport outage. */ }
            finally { this.checking = false; }
        },
        reminder() { try { return JSON.parse(localStorage.getItem(this.reminderKey)); } catch (_) { return null; } },
        async tick() {
            if (this.onLogin || this.locked || !claims(this.scope)) return;
            const now = Date.now();
            if (this.status?.session_expires_at && new Date(this.status.session_expires_at).getTime() <= now) { lockSession(this.scope); return; }
            if (this.status?.idle?.enabled && this.status.last_activity_at && Math.max(new Date(this.status.last_activity_at).getTime(), this.lastInteraction) + this.status.idle.minutes * 60000 <= now) {
                lockSession(this.scope); return;
            }
            if (!document.hidden && now - this.lastInteraction < 30000) {
                const saved = this.reminder();
                if (saved && now - saved.tick >= 1000) {
                    saved.remaining -= Math.min(1000, now - saved.tick); saved.tick = now;
                    localStorage.setItem(this.reminderKey, JSON.stringify(saved));
                    if (saved.remaining <= 0 && this.needsTelegram) this.showReminder = true;
                }
                if (now - this.lastActivitySent > 20000) {
                    this.lastActivitySent = now;
                    securityRequest(this.scope, 'activity', {}).catch(() => {});
                }
            }
            const current = claims(this.scope);
            if (now - this.lastCheck > 30000 && !this.checking) {
                if (current && current.exp * 1000 < now + 60000) {
                    try { await refreshSession(this.scope); } catch (_) { this.lastCheck = now; return; }
                }
                this.check();
            }
        },
        dismiss() { localStorage.setItem(this.reminderKey, JSON.stringify({remaining: 30 * 60000, tick: Date.now()})); this.showReminder = false; },
        async enroll() {
            this.busy = true; this.error = '';
            try { this.enrollment = await securityRequest(this.scope, 'enroll', {}); }
            catch (error) { this.error = error.response?.data?.msg || 'Не удалось открыть подключение.'; }
            finally { this.busy = false; }
        },
        connected() { this.enrollment = null; this.check(); },
        trapFocus(event) {
            const nodes = event.currentTarget.querySelectorAll('input:not([disabled]),button:not([disabled]),a[href]');
            if (!nodes.length) return;
            if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes[nodes.length - 1].focus(); }
            else if (!event.shiftKey && document.activeElement === nodes[nodes.length - 1]) { event.preventDefault(); nodes[0].focus(); }
        }
    }
};
</script>
<style>
.session-shield,.telegram-reminder-dialog { position: fixed; inset: 0; z-index: 2147483600; display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; padding: 24px 12px; background: #f6f9fb; }
.telegram-reminder-dialog { background: #1b344c55; z-index: 2000; }
.session-shield__card { margin: auto 0; width: min(100%, 500px); padding: 18px; background: #fbfefd; border: 1px solid #dce7e8; border-radius: 14px; box-shadow: inset 0 1px white, 0 12px 40px #173f5b12; }
.telegram-reminder { position: fixed; bottom: 10px; right: 12px; z-index: 1040; max-width: calc(100vw - 24px); }
.argus-security-locked .modal,.argus-security-locked .b-sidebar-outer { visibility: hidden !important; }
</style>
